import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { FinanceStatCard } from '@/modules/finance/components/FinanceStatCard';
import { toast } from 'sonner';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  Receipt,
  FileText,
  ArrowRight,
  PiggyBank,
  AlertTriangle,
  CheckCircle2,
  Clock,
  RefreshCw } from
'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  ComposedChart,
  Line } from
'recharts';

import { financeAPI } from '@/shared/services/api';

const quickActions = [
{ name: 'New Invoice', href: '/finance/receivable', icon: FileText },
{ name: 'Record Payment', href: '/finance/payments', icon: CreditCard },
{ name: 'Submit Expense', href: '/finance/expenses', icon: Receipt },
{ name: 'View Reports', href: '/finance/reports', icon: TrendingUp }];


export const FinanceDashboard = () => {const { t } = useTranslation("finance");
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const data = await financeAPI.getDashboard();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      setDashboardData({
        stats: { total_balance: 0, accounts_receivable: 0, accounts_payable: 0, pending_expenses: 0 },
        accounts: [],
        cash_flow: [],
        recent_transactions: [],
        overdue_invoices: [],
        pending_approvals: []
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500" />
      </div>);

  }

  const stats = dashboardData?.stats || {};
  const accounts = dashboardData?.accounts || [];
  const cashFlow = dashboardData?.cash_flow || [];
  const recentTransactions = dashboardData?.recent_transactions || [];
  const overdueInvoices = dashboardData?.overdue_invoices || [];
  const pendingApprovals = dashboardData?.pending_approvals || [];

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("financeDashboard", "Finance Dashboard")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("financialOverviewAnd", "Financial overview and management")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Link to="/finance/reports">
            <Button className="gap-2 bg-green-600 hover:bg-green-700">
              <TrendingUp className="h-4 w-4" />{t("viewReports", "View Reports")}

            </Button>
          </Link>
        </div>
      </div>

      {error &&
      <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
          <p className="text-yellow-500 text-sm">{t("usingCachedData", "Using cached data -")}{error}</p>
        </div>
      }

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <FinanceStatCard
          title="Total Balance"
          value={stats.total_balance || 0}
          prefix="$"
          icon={Wallet}
          color="green" />
        
        <FinanceStatCard
          title="Accounts Receivable"
          value={stats.accounts_receivable || 0}
          prefix="$"
          icon={TrendingUp}
          color="blue"
          subtitle="Outstanding invoices" />
        
        <FinanceStatCard
          title="Accounts Payable"
          value={stats.accounts_payable || 0}
          prefix="$"
          icon={TrendingDown}
          color="amber"
          subtitle="Bills to pay" />
        
        <FinanceStatCard
          title="Pending Expenses"
          value={stats.pending_expenses || 0}
          prefix="$"
          icon={Receipt}
          color="purple"
          subtitle="Awaiting approval" />
        
      </div>

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-4">
        {quickActions.map((action) =>
        <Link key={action.name} to={action.href}>
            <Card className="hover:border-green-500 transition-colors cursor-pointer h-full">
              <CardContent className="pt-6 flex flex-col items-center text-center">
                <action.icon className="h-8 w-8 text-green-500 mb-3" />
                <h3 className="font-semibold">{action.name}</h3>
              </CardContent>
            </Card>
          </Link>
        )}
      </div>

      {/* Cash Flow Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">{t("cashFlow", "Cash Flow")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={cashFlow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="month" stroke="#9ca3af" />
              <YAxis stroke="#9ca3af" />
              <Tooltip
                contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
                formatter={(value) => [`$${value.toLocaleString()}`, '']} />
              
              <Bar dataKey="income" fill="#22c55e" name="Income" radius={[4, 4, 0, 0]} />
              <Bar dataKey="expenses" fill="#ef4444" name="Expenses" radius={[4, 4, 0, 0]} />
              <Line type="monotone" dataKey="net" stroke="#3b82f6" strokeWidth={2} name="Net" />
            </ComposedChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Two Column Layout */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Accounts Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">{t("accounts", "Accounts")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {accounts.length === 0 ?
              <p className="text-center text-muted-foreground py-4">{t("noAccounts", "No accounts")}</p> :

              accounts.map((account) =>
              <div key={account.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${account.type === 'checking' ? 'bg-green-500/20' :
                  account.type === 'savings' ? 'bg-blue-500/20' :
                  account.type === 'receivable' ? 'bg-amber-500/20' : 'bg-red-500/20'}`
                  }>
                        <Wallet className={`h-4 w-4 ${account.type === 'checking' ? 'text-green-500' :
                    account.type === 'savings' ? 'text-blue-500' :
                    account.type === 'receivable' ? 'text-amber-500' : 'text-red-500'}`
                    } />
                      </div>
                      <div>
                        <p className="font-medium">{account.name}</p>
                        <p className="text-sm text-muted-foreground capitalize">{account.type}</p>
                      </div>
                    </div>
                    <span className="font-semibold">${account.balance.toLocaleString()}</span>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>

        {/* Overdue Invoices */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />{t("overdueInvoices", "Overdue Invoices")}

            </CardTitle>
            <Link to="/finance/receivable">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {overdueInvoices.length === 0 ?
              <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("noOverdueInvoices", "No overdue invoices!")}</p>
                </div> :

              overdueInvoices.map((invoice) =>
              <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <div>
                      <p className="font-medium">{invoice.invoice_number}</p>
                      <p className="text-sm text-muted-foreground">{invoice.vendor_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${invoice.amount.toLocaleString()}</p>
                      <p className="text-xs text-red-400">{t("due", "Due:")}
                    {new Date(invoice.due_date).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>

        {/* Recent Transactions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold">{t("recentTransactions", "Recent Transactions")}</CardTitle>
            <Link to="/finance/transactions">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recentTransactions.length === 0 ?
              <p className="text-center text-muted-foreground py-4">{t("noRecentTransactions", "No recent transactions")}</p> :

              recentTransactions.slice(0, 5).map((txn) =>
              <div key={txn.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-800/50">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${txn.type === 'income' ? 'bg-green-500/20' : 'bg-red-500/20'}`
                  }>
                        {txn.type === 'income' ?
                    <TrendingUp className="h-4 w-4 text-green-500" /> :

                    <TrendingDown className="h-4 w-4 text-red-500" />
                    }
                      </div>
                      <div>
                        <p className="font-medium">{txn.reference}</p>
                        <p className="text-sm text-muted-foreground truncate max-w-[150px]">
                          {txn.description}
                        </p>
                      </div>
                    </div>
                    <span className={`font-semibold ${txn.type === 'income' ? 'text-green-500' : 'text-red-500'}`
                }>
                      {txn.type === 'income' ? '+' : '-'}${txn.amount.toLocaleString()}
                    </span>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Clock className="h-5 w-5 text-amber-500" />{t("pendingApprovals", "Pending Approvals")}

            </CardTitle>
            <Link to="/finance/expenses">
              <Button variant="ghost" size="sm" className="gap-2">{t("viewAll", "View All")}
                <ArrowRight className="h-4 w-4" />
              </Button>
            </Link>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingApprovals.length === 0 ?
              <div className="text-center py-8">
                  <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-2" />
                  <p className="text-muted-foreground">{t("noPendingApprovals", "No pending approvals!")}</p>
                </div> :

              pendingApprovals.map((expense) =>
              <div key={expense.id} className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div>
                      <p className="font-medium">{expense.expense_number}</p>
                      <p className="text-sm text-muted-foreground">{expense.employee_name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold">${expense.amount.toLocaleString()}</p>
                      <Badge variant="secondary">{expense.category}</Badge>
                    </div>
                  </div>
              )
              }
            </div>
          </CardContent>
        </Card>
      </div>
    </div>);

};

export default FinanceDashboard;